import { useCallback, useEffect, useRef } from "react";
import { useAssetActivateFetcher } from "./activate";
import useAssetAvailability from "./useAssetAvailability";
import useToasts from "../../../../root/core-ui/toast/useToasts";
import { FormattedMessage, useIntl } from "react-intl";
import AssetCoverToast from "../../../../../common/features/asset/AssetCoverToast.tsx";
import BaseLinkOrButton from "../../../../../common/core-ui/BaseLinkOrButton.tsx";
import { getReaderNavURL } from "../../../../bookshelfLinks.ts";
import styles from "./useAssetActivate.module.scss";
import { clsx } from "clsx";

function useAssetActivate(asset?: VST.Bridge.Asset): () => void | null {
  const fetcher = useAssetActivateFetcher();
  const { getMatchingCredits } = useAssetAvailability(asset);
  const { showToast } = useToasts();
  const intl = useIntl();
  const shouldShowToastRef = useRef(false);

  const doActivate = useCallback(() => {
    const { matchingUserCredit } = getMatchingCredits();

    if (!asset?.active && matchingUserCredit && fetcher.state === "idle") {
      shouldShowToastRef.current = true;
      fetcher.submit(
        { credit: matchingUserCredit.id },
        {
          action: `/actions/activate/${asset?.vbid}`,
          method: "POST",
        }
      );
    }
  }, [fetcher, asset, getMatchingCredits]);

  useEffect(() => {
    if (
      asset &&
      shouldShowToastRef.current &&
      fetcher.state === "loading" &&
      typeof fetcher.data?.ok === "boolean"
    ) {
      shouldShowToastRef.current = false;
      if (fetcher.data.ok) {
        showToast(
          <AssetCoverToast
            vbid={asset.vbid}
            type="success"
            title={intl.formatMessage(
              { defaultMessage: "{title} has been added to My Library." },
              { title: asset.title }
            )}
          >
            <BaseLinkOrButton
              className={clsx("vst-link", styles.toastLink)}
              href={getReaderNavURL(asset.vbid)}
            >
              <FormattedMessage defaultMessage="Open Book" />
            </BaseLinkOrButton>
          </AssetCoverToast>
        );
      } else {
        showToast(
          <AssetCoverToast
            vbid={asset.vbid}
            type="error"
            title={intl.formatMessage(
              {
                defaultMessage: "An error occurred borrowing {title}.",
              },
              { title: asset.title }
            )}
          />
        );
      }
    }
  }, [intl, showToast, asset, fetcher]);

  return doActivate;
}

export default useAssetActivate;
